'use client'

import { useState } from 'react'
import { Product } from '@/lib/types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { GradientButton } from '@/components/ui/gradient-button'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Checkbox } from '@/components/ui/checkbox'
import Image from 'next/image'
import Link from 'next/link'
import {
  Package,
  Layers,
  Cpu,
  FileText,
  ExternalLink,
  Scale,
  Heart,
  ShoppingCart,
  Info,
  CheckCircle,
  Clock,
  Box,
  Zap,
  Thermometer,
  AlertCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { useComparison } from '@/lib/comparison-context'
import { useWishlist } from '@/lib/wishlist-context'
import { useQuoteCart } from '@/lib/quote-cart-context'
import { cn } from '@/lib/utils'

interface ProductQuickViewProps {
  product: Product
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProductQuickView({ product, open, onOpenChange }: ProductQuickViewProps) {
  const [selectedImage, setSelectedImage] = useState(0)
  const { isInComparison, addToComparison, removeFromComparison, comparedProducts } = useComparison()
  const { isInWishlist, toggleWishlist } = useWishlist()
  const { addToQuoteCart } = useQuoteCart()
  
  const isCompared = isInComparison(product.id)
  const canAddMore = comparedProducts.length < 4
  const isWishlisted = isInWishlist(product.id)

  const handleRequestQuote = () => {
    addToQuoteCart(product, 1)
    toast.success(`${product.partNumber} added to quote request`, {
      description: 'View your quote cart to continue',
      action: {
        label: 'View Cart',
        onClick: () => {
          onOpenChange(false)
          window.location.href = '/quote'
        }
      }
    })
  }

  const handleCompareToggle = () => {
    if (isCompared) {
      removeFromComparison(product.id)
      toast.success('Removed from comparison')
    } else if (canAddMore) {
      addToComparison(product)
      toast.success('Added to comparison')
    } else {
      toast.error('Maximum 4 products can be compared')
    }
  }

  const handleWishlistToggle = () => {
    toggleWishlist(product)
    if (isWishlisted) {
      toast.success('Removed from wishlist')
    } else {
      toast.success('Added to wishlist')
    }
  }

  const handleAddToCart = () => {
    toast.success(`Added ${product.partNumber} to cart`)
  }

  // Group specifications by category
  const groupedSpecs = Object.entries(product.specifications || {}).reduce((acc, [key, value]) => {
    // Categorize specs
    const category = categorizeSpec(key)
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push({ key, value: String(value) })
    return acc
  }, {} as Record<string, Array<{ key: string; value: string }>>)

  function categorizeSpec(key: string): string {
    const electrical = ['voltage', 'current', 'power', 'resistance', 'capacitance', 'inductance', 'frequency']
    const physical = ['package', 'mounting', 'dimension', 'weight', 'size', 'height', 'width', 'length']
    const thermal = ['temperature', 'thermal', 'operating', 'temp']
    
    const lowerKey = key.toLowerCase()
    
    if (electrical.some(term => lowerKey.includes(term))) return 'Electrical'
    if (physical.some(term => lowerKey.includes(term))) return 'Physical'
    if (thermal.some(term => lowerKey.includes(term))) return 'Environmental'
    
    return 'General'
  }

  // Get category icon
  function getCategoryIcon(category: string) {
    switch (category) {
      case 'Electrical':
        return <Zap className="w-4 h-4 text-amber-500" />
      case 'Physical':
        return <Box className="w-4 h-4 text-blue-500" />
      case 'Environmental':
        return <Thermometer className="w-4 h-4 text-red-500" />
      default:
        return <Info className="w-4 h-4 text-slate-500" />
    }
  }

  // Safe image handling
  const productImages = product.images?.length > 0 ? product.images : ['/placeholder.jpg']
  const currentImage = productImages[selectedImage] || productImages[0] || '/placeholder.jpg'

  // Safe specifications count
  const specsCount = Object.keys(product.specifications || {}).length
  const hasSpecs = specsCount > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] p-0 gap-0 overflow-hidden">
        <div className="max-h-[95vh] overflow-y-auto">
          <div className="p-6 pb-2">
            <DialogHeader>
              <div className="flex items-start justify-between gap-4 pr-8">
                <div className="flex-1 min-w-0">
                  <DialogTitle className="text-2xl font-bold text-slate-900 line-clamp-2">
                    {product.partNumber}
                  </DialogTitle>
                  <DialogDescription className="text-base mt-1">
                    by <span className="font-medium text-slate-700">{product.manufacturer}</span>
                  </DialogDescription>
                </div>
                
                {/* Stock Badge */}
                <Badge 
                  variant={product.stock === 'In Stock' ? 'default' : 'destructive'}
                  className="shrink-0"
                >
                  {product.stock}
                </Badge>
              </div>
            </DialogHeader>
          </div>

          <div className="px-6 pb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Images */}
              <div className="space-y-4">
                {/* Main Image */}
                <div className="relative aspect-square bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
                  <Image
                    src={currentImage}
                    alt={product.title}
                    fill
                    className="object-contain p-8"
                    priority
                  />
                  
                  {/* RoHS Badge */}
                  {product.rohs && (
                    <div className="absolute top-3 right-3">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        RoHS
                      </Badge>
                    </div>
                  )}

                  {/* Lifecycle Badge */}
                  {product.lifecycle && product.lifecycle !== 'Active' && (
                    <div className="absolute top-3 left-3">
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {product.lifecycle}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Thumbnail Images */}
                {productImages.length > 1 && (
                  <div className="w-full overflow-x-auto">
                    <div className="flex gap-2 pb-2">
                      {productImages.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={cn(
                            "relative w-20 h-20 bg-slate-50 rounded-md border-2 transition-all shrink-0 hover:border-slate-300",
                            selectedImage === index 
                              ? "border-[#518BC4] ring-2 ring-[#518BC4]/20 shadow-sm" 
                              : "border-slate-200"
                          )}
                        >
                          <Image
                            src={image}
                            alt={`${product.title} - ${index + 1}`}
                            fill
                            className="object-contain p-1.5"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Info Cards - Mobile */}
                <div className="grid grid-cols-2 gap-2 lg:hidden">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                      <Layers className="w-3 h-3" />
                      <span>Category</span>
                    </div>
                    <p className="font-medium text-sm text-slate-900 truncate">{product.category || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                      <Package className="w-3 h-3" />
                      <span>Package</span>
                    </div>
                    <p className="font-medium text-sm text-slate-900 truncate">{product.packageType || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                      <Info className="w-3 h-3" />
                      <span>MOQ</span>
                    </div>
                    <p className="font-medium text-sm text-slate-900">{product.moq || '1'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                      <Clock className="w-3 h-3" />
                      <span>Lead Time</span>
                    </div>
                    <p className="font-medium text-sm text-slate-900 truncate">{product.leadTime || 'Contact'}</p>
                  </div>
                </div>
              </div>

              {/* Right Column - Details */}
              <div className="space-y-4">
                {/* Title & Description */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2">
                    {product.title || product.partNumber}
                  </h3>
                  {product.description && (
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                      {product.description}
                    </p>
                  )}
                </div>

                {/* Key Info - Desktop */}
                <div className="hidden lg:grid grid-cols-2 gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2 text-sm">
                    <Layers className="w-4 h-4 text-slate-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-slate-500">Category</p>
                      <p className="font-medium text-slate-900 truncate">{product.category || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="w-4 h-4 text-slate-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-slate-500">Package</p>
                      <p className="font-medium text-slate-900 truncate">{product.packageType || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Info className="w-4 h-4 text-slate-500 shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500">MOQ</p>
                      <p className="font-medium text-slate-900">{product.moq || '1'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-slate-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-slate-500">Lead Time</p>
                      <p className="font-medium text-slate-900 truncate">{product.leadTime || 'Contact Sales'}</p>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  <GradientButton 
                    variant="blue-red"
                    className="flex-1"
                    onClick={handleRequestQuote}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Request Quote
                  </GradientButton>
                  <Button 
                    variant="outline"
                    size="icon"
                    onClick={handleWishlistToggle}
                    className={cn(
                      "shrink-0",
                      isWishlisted 
                        ? "bg-red-50 border-red-200 text-red-600 hover:bg-red-100" 
                        : "hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                    )}
                  >
                    <Heart className={cn("w-4 h-4", isWishlisted && "fill-current")} />
                  </Button>
                </div>

                {/* Compare Toggle */}
                <label className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-[#518BC4] hover:bg-slate-50 cursor-pointer transition-colors">
                  <Checkbox
                    checked={isCompared}
                    onCheckedChange={handleCompareToggle}
                    disabled={!canAddMore && !isCompared}
                    className="data-[state=checked]:bg-[#518BC4] data-[state=checked]:border-[#518BC4]"
                  />
                  <Scale className="h-4 w-4 text-slate-600 shrink-0" />
                  <span className="text-sm font-medium text-slate-700 flex-1">
                    {isCompared ? 'In Comparison' : 'Add to Comparison'}
                  </span>
                  {isCompared && (
                    <Badge variant="secondary" className="shrink-0">
                      {comparedProducts.length}/4
                    </Badge>
                  )}
                </label>

                <Separator />

                {/* Specifications Tabs */}
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="specs">
                      Specifications
                      {hasSpecs && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {specsCount}
                        </Badge>
                      )}
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="overview" className="space-y-3 mt-4">
                    {hasSpecs ? (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                          <Cpu className="w-4 h-4" />
                          Key Features
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(product.specifications)
                            .slice(0, 5)
                            .map(([key, value]) => (
                              <div key={key} className="flex items-start gap-2 text-sm p-2 rounded hover:bg-slate-50">
                                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <span className="text-slate-600">{key}: </span>
                                  <span className="font-medium text-slate-900 break-words">{String(value)}</span>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No detailed specifications available</p>
                      </div>
                    )}

                    {product.applications && product.applications.length > 0 && (
                      <div className="space-y-2 pt-3 border-t">
                        <h4 className="text-sm font-semibold text-slate-900">Applications</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {product.applications.map((app, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {app}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {product.tags && product.tags.length > 0 && (
                      <div className="space-y-2 pt-3 border-t">
                        <h4 className="text-sm font-semibold text-slate-900">Tags</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {product.tags.map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="specs" className="mt-4">
                    {hasSpecs ? (
                      <div className="h-[300px] overflow-y-auto pr-4">
                        <div className="space-y-4">
                          {Object.entries(groupedSpecs).map(([category, specs]) => (
                            <div key={category}>
                              <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                                {getCategoryIcon(category)}
                                {category}
                              </h4>
                              <div className="space-y-1">
                                {specs.map(({ key, value }) => (
                                  <div
                                    key={key}
                                    className="flex justify-between items-start text-sm py-2 px-3 rounded hover:bg-slate-50 gap-4"
                                  >
                                    <span className="text-slate-600 break-words">{key}</span>
                                    <span className="font-medium text-slate-900 text-right break-words flex-shrink-0 max-w-[50%]">
                                      {value}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-slate-500">
                        <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No specifications available</p>
                        <p className="text-xs mt-1">Contact sales for detailed specs</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                {/* View Full Details Link */}
                <Button
                  variant="outline"
                  className="w-full"
                  asChild
                  onClick={() => onOpenChange(false)}
                >
                  <Link href={`/products/${product.id}`}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Full Product Details
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
